import { Alert, AlertTitle, Box, Link, Stack, Typography } from "@mui/material";

import AccessRequests from "./AccessRequests";
import Activity from "./Activity";
import Apps from "./Apps";
import Connectors from "./Connectors";
import Costs from "./Costs";
import DataQuality from "./DataQuality";
import Design from "./Design";
import Installs from "./Installs";
import Objects from "./Objects";
import People from "./People";
import Portal from "./Portal";
import Requests from "./Requests";
import Unused from "./Unused";

/**
 * An honest placeholder.
 *
 * "Coming soon" tells a reader nothing they can act on. This says which
 * endpoint is missing and which card is going to add it, because the person
 * most likely to open an unbuilt section is somebody deciding what to build
 * next.
 */
function Pending({ title, needs, why }) {
  return (
    <Stack spacing={2}>
      <Alert severity="info" variant="outlined">
        <AlertTitle>Not built yet</AlertTitle>
        {why}
      </Alert>
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
          Waiting on
        </Typography>
        <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2.5 }}>
          {needs.map((n) => (
            <Typography component="li" variant="body2" key={n}>
              {n}
            </Typography>
          ))}
        </Stack>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Tracked on the{" "}
        <Link
          href="https://projects.lopie.dev/e65a64c7-8a93-415b-87f5-0c7c43babbf5"
          target="_blank"
          rel="noreferrer"
        >
          Kernel Console epic
        </Link>
        .
      </Typography>
    </Stack>
  );
}

const pending = (props) => () => <Pending {...props} />;

/**
 * The questions, in the order somebody actually asks them.
 *
 * Portal is first, and is therefore where the console opens, because LO-31
 * changed what this thing *is*. A governance console you could also run apps
 * from would put the apps at the bottom; a portal that governs puts them
 * first. `Pending apps` (LO-61) sits between what each app may touch today
 * and what happened, because it is the decision that turns the first into the
 * second.
 *
 * `Apps` sits second because it is the other half of Portal's question:
 * Portal answers *what is here and can I open it*, Apps answers *what is
 * here, what could be, and what may each one touch*.
 *
 * It used to be two screens and that was the mistake. "Library" listed what
 * you could install and what you had installed; "Apps" listed what each app
 * type was allowed to touch. So an app you had installed was a different kind
 * of thing on each, the two showed the same apps under two names in two
 * orders, and the screen called Apps — which is where anybody looks for their
 * apps — had no idea a deployment existed and no way to remove one.
 *
 * `Setup` (LO-94) is the newest and is the only one that is not a governance
 * question. It sits by Connectors because it asks the thing somebody asks
 * about thirty seconds after that screen: fine, data is flowing — is this app
 * plugged into any of it. That is operational rather than governed, decided
 * per deployment rather than per app type, and grants nothing; it is here
 * because an app nobody answered it for is indistinguishable from an app
 * working perfectly, on every other screen in this console.
 *
 * `Design` is last and is not one of them. It answers a question about this
 * console rather than about the kernel, and it is here because the rules a
 * micro-app is held to were legible to an agent through the MCP and to
 * nobody else — a design contract only its enforcers can read is a contract
 * the people governed by it cannot check.
 */
/**
 * Four jobs, not twelve questions.
 *
 * ## What was wrong with a flat list
 *
 * The twelve were ordered by how the system is built, which meant a reader
 * scanned a list of things the kernel can answer rather than a list of things
 * they came here to do. Nothing on it was wrong and nothing on it is dropped:
 * the fix is that `Costs` sits beside the apps it is charging for, and
 * `Unused` sits beside the log it is reconciling against, because that is
 * where somebody looks for each.
 *
 * ## One audience
 *
 * This console is for platform administration. There is no staff user in it
 * — nobody signs in here to open their password vault — so `Portal` is an
 * administrator asking *what is deployed and does it come up*, which is why
 * it groups with `Apps` and `Costs` rather than standing alone as a launcher.
 *
 * ## The two calls worth arguing with
 *
 * `Pending apps` is under **Access** and not under **Estate**. Approving one
 * does end in an install, but the decision being made is *should they be
 * allowed this*, and that is the same decision `People` is about.
 *
 * `Design` is under **Assurance** and not in a group of its own. It reads as
 * documentation and it is doing something else: it states what this platform
 * claims about itself and then shows which of those claims are enforced and
 * which are review-only. That is the shape of `Integrity` — is the thing
 * telling the truth — applied to the design contract instead of to the log.
 */
export const GROUPS = [
  {
    id: "estate",
    label: "Estate",
    blurb: "What is deployed here, and what it costs to run.",
  },
  {
    id: "data",
    label: "Data",
    blurb: "Where it comes from, which app is plugged into it, what is in it.",
  },
  {
    id: "access",
    label: "Access",
    blurb: "Who may see what, and who is asking for more.",
  },
  {
    id: "assurance",
    label: "Assurance",
    blurb: "What happened, what was never used, and whether this platform"
      + " does what it says.",
  },
];

export const SECTIONS = [
  {
    id: "portal",
    group: "estate",
    label: "Portal",
    question: "What is provisioned here, and can I open it?",
    component: Portal,
    // Not tag-gated: this lists client pointers, not records.
    gate:
      "Not limited by your groups: this lists the apps installed here and" +
      " where each one opens from, and everything listed is yours. It says" +
      " nothing about whether an app is healthy or running — opening it is" +
      " how you find that out. An app you open is judged by what it asked" +
      " for and what you are allowed to see, never by what this console can" +
      " see.",
  },
  {
    id: "apps",
    group: "estate",
    label: "Apps",
    question: "What is installed, and what may each one touch?",
    component: Apps,
    // The only screen that reads two APIs. The kernel says what an app may
    // touch; the control plane says what is running and is the only thing
    // that can change it. One screen, because which of the two answers a
    // question is not something a person should have to know before they can
    // find their apps.
    gate:
      "Not limited by your groups: every app here is listed for everyone who" +
      " can sign in, and everything listed belongs to you. No record, count" +
      " or user's groups appear. Installing is the biggest thing any screen" +
      " here does — it builds real infrastructure in your organization's own" +
      " AWS account and costs money to run — and it hands out no access: an" +
      " app you install can still only see what it asked for and what you" +
      " are allowed to see.",
  },
  {
    id: "connectors",
    group: "data",
    label: "Connectors",
    question: "Is data flowing, and can I plug in more?",
    component: Connectors,
    // Not tag-gated, and saying so is better than an empty intersection.
    gate:
      "Governed by having a manifest, not by tags, for everything except" +
      " one button. This asks who wrote into the graph — a fact about the" +
      " store rather than about any record — so there is no type whose tags" +
      " could narrow it. Rolling a run back is the exception: it rewrites" +
      " records a connector already wrote, so it additionally needs" +
      " `data_quality.review` and the person doing it to be on this kernel's" +
      " committed governor list — the same two-gate shape `manifest.review`" +
      " uses for a manifest decision.",
  },
  {
    id: "installs",
    group: "data",
    label: "Setup",
    question: "Where does each app's data come from?",
    component: Installs,
    // Beside Connectors rather than beside Apps, and the placement is the
    // argument. Apps answers *what may this touch* — a governance question,
    // decided once per app type and reviewed. This answers *is this actually
    // plugged in*, which is operational, decided per deployment at install,
    // and is the question somebody asks about thirty seconds after the one
    // Connectors answers.
    gate:
      "Governed by having a manifest to read, and by one declared action," +
      " `install.configure`, to answer. Choosing a source grants nothing and" +
      " widens nothing: what an app may read is still what it asked for" +
      " and what you hold, whatever is chosen here, and pointing an app at" +
      " the directory hands" +
      " it no record it could not already reach. What it changes is what an" +
      " empty screen means — without an answer, no data and working-as-" +
      "intended are the same picture. No record or count appears here; which" +
      " connectors could satisfy a requirement is derived from the concept" +
      " registry, so one added to the kernel becomes an option with no" +
      " change to this screen.",
  },
  {
    id: "data-quality",
    group: "data",
    label: "Data quality",
    question: "What is waiting on a person to decide?",
    component: DataQuality,
    // Not tag-gated: a held run, a held reject, a possible duplicate and a
    // low-confidence category are facts about the pipe and the model, not
    // about any one record's tags.
    gate:
      "Governed by one declared action, `data_quality.review`, for reading" +
      " the queue. Accepting an item, checking for new matches or" +
      " categories, and rolling a run back are all writes — a confirmed" +
      " duplicate, an applied category, real TypeSafe spend, or a rewritten" +
      " record — so each additionally requires the person doing it to be" +
      " on this kernel's committed governor list, the same two-gate shape" +
      " `manifest.review` uses for a manifest decision.",
  },
  {
    id: "objects",
    group: "data",
    // Renamed from "Objects" per Jason's own framing in the 2026-09-23 prep
    // call: this is the whole thing the business runs on, and it earns a
    // word a non-technical audience meets somewhere other than a database
    // course. `id` stays "objects" — the URL hash and every deep link to it
    // survive the rename; only what a reader sees changes.
    label: "Brain",
    question: "What does this business know, and how does it connect?",
    component: Objects,
  },
  {
    id: "costs",
    group: "estate",
    label: "Costs",
    question: "What is this costing, and for what?",
    component: Costs,
    // Not tag-gated: a bill is not a record about a customer and carries no
    // tags. What it reads is two types the manifest declares, and the reason
    // it is here rather than on the Apps screen is that "what am I spending"
    // is a question somebody arrives with — it earns a headline, and the app
    // cards are already dense with what each app may touch.
    gate:
      "Governed by this app's manifest and not by your groups. Cloud spend" +
      " carries no tags — it is what AWS charged for the resources an" +
      " instance runs on, not a record about anybody — so there is no" +
      " intersection to narrow here and everybody who can open this screen" +
      " sees the same figures. Agent spend is read from the agent register" +
      " alongside it. Neither is a record this console could write: the daily" +
      " cost job writes one through the same connector pipeline every" +
      " connector uses, and the agents write their own.",
  },
  {
    id: "people",
    group: "access",
    label: "People",
    question: "Who can see what, and who gets told?",
    component: People,
    // Two registers until now — Access and Users — and they read as
    // duplicates because both listed people and each had its own way of
    // adding one. They were not duplicates: they are two facts about one
    // person, split along the boundary of the system that stores each fact
    // rather than the boundary of what a reader is looking at.
    //
    // Joined for display and deliberately not in storage. Access reads
    // through to Cognito and is the set of people who can sign in; the roster
    // is the kernel's own and holds people with no login at all. Folding them
    // would mean either refusing to notify anybody without an account, or
    // minting accounts to hold addresses — and `users.py` gives the sharper
    // version: a group is a tag, not a subscription, so overloading one into
    // the other would grant somebody access to records in order to email
    // them.
    //
    // Not tag-gated: this lists people, not records. The counting *is*
    // tag-shaped and happens inside the grant preview, where the numbers are.
    gate:
      "Governed by five declared actions and not by tags. `access.preview`" +
      " counts what a grant would expose — including records you may not be" +
      " able to open yourself, which is the whole feature and a real" +
      " disclosure, bounded by this app's own ceiling and never beyond it." +
      " `access.grant` changes a group membership and lands in the audit" +
      " log; granting needs a second gate on top, because the person doing" +
      " it must also be on this kernel's committed governor list, so the app" +
      " declaring the action is necessary and no longer sufficient. The" +
      " other three read the notification roster, change it, and take" +
      " somebody off — separate grants, because a name on that roster" +
      " receives notifications and can open nothing. The two halves of this" +
      " screen are two different populations and two different acts: nothing" +
      " here lets subscribing somebody to an alert widen what they can read.",
  },
  {
    id: "requests",
    group: "access",
    label: "Pending apps",
    question: "Who is asking for what, and should they have it?",
    component: Requests,
    // Not tag-gated: this lists proposals, not records. Gated harder than the
    // other registers, and the difference is worth stating rather than
    // smoothing over.
    gate:
      "Governed by one declared action, `manifest.review`, not by tags and" +
      " not merely by having a manifest. /apps and /instances list" +
      " declarations already in force and any known app may read them; this" +
      " lists what apps have *asked for*, with contact details and prose" +
      " about why. Reading the queue needs only that action. Deciding one" +
      " needs a second gate: approving writes a manifest and" +
      " rejecting ends a proposal for good, so both additionally require the" +
      " person to be on this kernel's committed governor list — a list of" +
      " people, deliberately not a group, because here a group name is a" +
      " tag and a `governors` tag would widen reads.",
  },
  {
    id: "access-requests",
    group: "access",
    label: "Access requests",
    question: "Who is waiting for a tag, and should they have it?",
    component: AccessRequests,
    // Not tag-gated: this lists requests, not records.
    gate:
      "Governed by two halves of one grant and not by tags. Anybody signed" +
      " into an app can ask for a tag it needs, and asking grants nothing." +
      " Seeing everybody's requests and deciding them needs this app to" +
      " declare `access.grant` and you to be on this kernel's committed" +
      " governor list, because approving is that grant, performed as you with" +
      " the request as its reason. Anybody else sees only their own requests." +
      " A tag cannot be decided here unless this app declares it, since an app" +
      " may not hand out what it could not be shown itself.",
  },
  {
    id: "activity",
    group: "assurance",
    label: "Activity",
    question: "What happened, and who did it?",
    component: Activity,
    // Not tag-gated, and the difference from every other screen is worth
    // stating rather than smoothing over: this one deliberately reaches past
    // `manifest ∩ user`.
    gate:
      "Governed by one declared action, `audit.read`, and not by tags. The" +
      " log is returned whole — including rows naming records you cannot" +
      " open — because a log narrowed to what you may see can hide the thing" +
      " you are investigating. It names records rather than opening them:" +
      " reading one still goes through its own collection, limited the same" +
      " way as everything else. Not gated on being a governor, deliberately" +
      " — an audit" +
      " log only a governor may open is one the governed cannot use to check" +
      " the governors. It is served separately, by something that can read" +
      " the log and is denied every write to it — which is also" +
      " why reading it leaves no row in it.",
  },
  {
    id: "unused",
    group: "assurance",
    label: "Unused",
    question: "What may they do that they never have?",
    component: Unused,
    gate:
      "Governed by two declared actions and not by tags. It reads every" +
      " app's ceiling, which any app with a manifest may do, and then asks" +
      " the log one question per verb under `audit.read`. Nothing on this" +
      " screen is a record: it is a set of declarations laid against a set of" +
      " partition reads. The usage answer is per verb and never per app —" +
      " asking which of two apps used a verb means filtering after the read," +
      " which walks a whole partition and can stop early, and this screen" +
      " reports an exact answer or says it cannot tell.",
  },
  {
    id: "integrity",
    group: "assurance",
    label: "Integrity",
    question: "Is the kernel telling the truth?",
    component: pending({
      why: "The bypass check runs daily and asks one question: is there a row in the graph that nothing says who wrote. Its answer is published as a metric (RowsScanned and UnstampedRows, in LopieOne/Kernel) and this console cannot read it yet, so this screen says nothing about what it found rather than repeat a figure that would go stale.",
      needs: [
        "Somewhere for this console to read that answer from.",
        "A check that the log's rows agree with each other. Until that lands, the honest line is that a row cannot be deleted and can be overwritten — which this screen has to say rather than show a green tick.",
      ],
    }),
  },
  {
    id: "design",
    group: "assurance",
    label: "Design",
    question: "What does this system say, and what does it actually enforce?",
    component: Design,
    // Last, and not a governance question. It is here because the rules a
    // micro-app is held to were readable by an agent through the MCP and by
    // nobody else, and a design contract only its enforcers can read is a
    // contract the people governed by it cannot check.
    gate:
      "Governed by nothing. Every word on this screen is read from files in" +
      " the repo — packages/kernel-ui/README.md and the theme — and not one" +
      " of them is a record. No kernel request is made, no tag is involved," +
      " and the component gallery is fabricated on purpose and says so.",
  },
];
